import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import cuid from 'cuid';
import { get, isEqual } from 'lodash';

import { Modal } from '../Modal';
import * as API from '../../resources/api';
import { ReactComponent as CheckIcon } from '../../icons/icon-check.svg';
import { ReactComponent as CrossIcon } from '../../icons/icon-cross.svg';
import { ReactComponent as LoadingIcon } from '../../icons/icon-loading.svg';
import { prettyBytes } from '../../helpers/pretty-bytes';

import '../../css/FileField.scss';

const UPLOAD_SUCCESS_MESSAGE_DURATION = 3000;
const isImageFile = (x) => (x.type || '').split('/')[0] === 'image';
const isVideoFile = (x) => (x.type || '').split('/')[0] === 'video';

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'left',
  padding: 'var(--space-5)',

  borderWidth: 1,
  borderRadius: '3px',
  borderColor: 'var(--black-tint-70)',
  borderStyle: 'dashed',
  backgroundColor: 'var(--black-tint-95)',
  color: 'var(--black-tint-40)',
  outline: 'none',
  transition: 'border .24s ease-in-out',
  cursor: 'pointer',
};

const fileUploadHoverStyles = {
  borderColor: 'var(--black-tint-40)',
  backgroundColor: 'var(--black-tint-90)',
};

const modalStyle = {
  backgroundColor: 'white',
  overflow: 'auto',
  padding: 'var(--space-7)',
  maxHeight: '90vh',
  maxWidth: '90vw',
};

const imagePreviewStyles = {
  padding: 'var(--space-3)',
  // padding: 'var(--space-2) var(--space-3) var(--space-3)',
  boxShadow:
    '0px 0.5px 4px 0px rgba(1, 1, 1, 0.12), 0px 1.5px 2px 0px rgba(1, 1, 1, 0.12), 0px 1.5px 1.5px 0px rgba(1, 1, 1, 0.12)',
  width: 'fit-content',
};

const fullPreviewSizeStyles = {
  cursor: 'pointer',
  width: '100%',
  maxWidth: '600px',
  maxHeight: '400px',
  objectFit: 'contain',
};

const alwaysUnderlinedLinkStyle = {
  color: 'var(--blue-shade-20)',
  textDecoration: 'underline',
};

const uploadFile = async (file, doc, uuid) => {
  const size = prettyBytes(file.size);
  const fileData = {
    placeholder: true,
    type: file.type,
    filename: file.path,
    size,
    id: cuid(),
  };
  // Submit placeholder
  doc.submitOp({ p: [uuid, 99999], li: fileData });

  const uploadDestRes = await API.getFileUploadDestination(
    fileData.id,
    fileData.type
  );
  const { uploadUrl, fileUrl } = uploadDestRes.data;
  await fetch(uploadUrl, {
    method: 'PUT',
    headers: new Headers({ 'Content-Type': file.type }),
    body: file,
  });

  fileData.url = fileUrl;
  delete fileData.placeholder;
  const placeholder = doc.data[uuid].find((fd) => fd.id === fileData.id);
  const placeholderIndex = doc.data[uuid].indexOf(placeholder);

  // Replace placeholder with actual image URL
  if (placeholder) {
    doc.submitOp({
      p: [uuid, placeholderIndex],
      li: fileData,
      ld: placeholder,
    });
  } else {
    // If the placeholder got deleted during upload, we still just insert the image
    doc.submitOp({
      p: [uuid, 9999],
      li: fileData,
    });
  }
};

const onDrop = async (files, doc, uuid) => {
  try {
    await Promise.all(files.map((file) => uploadFile(file, doc, uuid)));
  } catch (e) {
    const placeholders = doc.data[uuid].filter((fd) => fd.placeholder);
    placeholders.forEach((p) =>
      doc.submitOp({ p: [uuid, doc.data[uuid].indexOf(p)], ld: null })
    );
    alert('Failed uploading file, please check your connection and try again.');
  }
};

const FilePreview = ({
  fileData,
  doc,
  onClickPreview,
  onClickDelete,
  condensed = false,
  ...props
}) => {
  const { placeholder, url } = fileData;
  const isImage = isImageFile(fileData);
  const isVideo = isVideoFile(fileData);
  const [recentlyUploaded, setRecentlyUploaded] = useState(placeholder);
  const showUploadSuccessful = recentlyUploaded && !placeholder;
  useEffect(() => {
    // The change from placeholder to non-placeholder triggers the notification
    // after which we activate a timeout to disable it again
    if (showUploadSuccessful) {
      setTimeout(
        () => setRecentlyUploaded(false),
        UPLOAD_SUCCESS_MESSAGE_DURATION
      );
    }
  }, [showUploadSuccessful]);

  return (
    <div
      // The 26 extra pixels are the height of the top bar for the preview
      style={condensed ? {} : imagePreviewStyles}
      {...props}
    >
      <div className="flex items-center body-s mb2 uploaded-file-text">
        <span className="flex items-center">
          {placeholder ? (
            <span className="bold uploading">
              Uploading "{fileData.filename}"
            </span>
          ) : (
            <a
              className={`bold uploaded-file-link ${
                showUploadSuccessful ? 'uploading' : ''
              }`}
              target="_blank"
              rel="noopener noreferrer"
              href={fileData.url}
            >
              {fileData.filename}
            </a>
          )}
          {/* Avoid shrinking the size display as we want the full value to render properly. */}
          <span className="text-black-tint-40" style={{ flexShrink: 0 }}>
            &nbsp;({fileData.size})
          </span>
        </span>
        {!condensed && (
          <span className="flex items-center" style={{ marginLeft: 'auto' }}>
            {showUploadSuccessful && (
              <span
                className="flex items-center mx4 text-black-tint-40 fade-out"
                style={{ left: 0 }}
              >
                <CheckIcon
                  className="mr2"
                  style={{ width: '12px', height: '12px' }}
                />
                Uploaded
              </span>
            )}
            {placeholder && <LoadingIcon />}
            <span
              onClick={onClickDelete}
              className="ml4 mt1 flex visible-on-hover"
              style={{ cursor: 'pointer' }}
            >
              <CrossIcon style={{ width: '12px', height: '12px' }} />
            </span>
          </span>
        )}
      </div>
      {!condensed && isImage && url && (
        <div style={fullPreviewSizeStyles}>
          <img
            onClick={onClickPreview}
            style={fullPreviewSizeStyles}
            src={url}
          />
        </div>
      )}
      {!condensed && isVideo && url && (
        <div style={fullPreviewSizeStyles}>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video controls style={fullPreviewSizeStyles} src={url} />
        </div>
      )}
    </div>
  );
};

export class FileField extends React.Component {
  constructor(props) {
    super(props);
    const { doc, data, uuid } = props;
    this.path = [uuid];

    const dataToPassOn = data || get(doc.data, this.path);
    this.state = { data: dataToPassOn };
  }

  componentDidMount() {
    const { doc } = this.props;

    doc.on('op', (op) => {
      if (
        op.some((operation) =>
          isEqual(operation.p.slice(0, operation.p.length - 1), this.path)
        )
      ) {
        this.setState({ data: get(doc.data, this.path) });
      }
    });
  }

  render() {
    // eslint-disable-next-line react/destructuring-assignment
    const data = this.props.data || this.state.data;

    return <FileFieldWithData {...this.props} data={data} />;
  }
}

const FileFieldWithData = ({
  doc,
  uuid,
  multiple,
  currentUser,
  data,
  readOnlyWorksheet,
  ...rest
}) => {
  const files = data || [];
  const isFirstImage = files.length <= 0;

  const { getRootProps, getInputProps, isDragAccept } = useDropzone({
    multiple: multiple !== 'false',
    noClick: !isFirstImage,
    onDrop: (files) => onDrop(files, doc, uuid),
    onDropRejected: (errs) =>
      errs.find((err) => err.errors.find((e) => e.code === 'too-many-files'))
        ? alert('Only a single file is allowed.')
        : alert('Error when selecting file. Please try again.'),
  });
  const [mediaModalIndex, setMediaModalIndex] = React.useState(false);
  const mediaModalIndexRef = React.useRef();
  mediaModalIndexRef.current = mediaModalIndex;

  if (!doc) {
    return null;
  }

  const isRollup = currentUser?.rollup;
  const allowUpload = !isRollup && !readOnlyWorksheet;
  const previewableFiles = files.filter(Boolean).filter(isImageFile);
  const dropzoneStyles = {
    ...baseStyle,
    ...(isDragAccept ? fileUploadHoverStyles : {}),
  };

  const previewFile = (file) =>
    setMediaModalIndex(previewableFiles.indexOf(file));
  const deleteFile = (index) => {
    const file = doc.data[uuid][index];
    if (file && file.id && file.url) {
      API.deleteFile(file.id);
    }
    doc.submitOp({ p: [uuid, index], ld: null });
  };

  return (
    <div>
      {/* Image List */}
      {!isFirstImage && (
        <div
          className={isRollup ? 'flex flex-wrap items-start justify-start' : ''}
        >
          {files.map((file, i) => (
            <FilePreview
              // It's extremely important that this key contains a unique ID that is preserved
              // during all upload stages, otherwise render order and callback association gets jumbled
              key={`img-${uuid}-${file.id}`}
              className={`mb4 hoverable ${isRollup ? 'mr4' : ''}`}
              fileData={file}
              doc={doc}
              condensed={isRollup}
              onClickDelete={() => deleteFile(i)}
              onClickPreview={() => previewFile(file)}
            />
          ))}
        </div>
      )}
      {/* Upload Zone */}
      {(isFirstImage || multiple !== 'false') && allowUpload && (
        <div
          {...getRootProps({ style: dropzoneStyles })}
          onClick={() => document.querySelector(`#input-${uuid}`).click()}
        >
          <input id={`input-${uuid}`} {...getInputProps()} />
          {/* TODO This should be a text-button from FDS */}
          <div className="flex items-center justify-center">
            <p>
              Drop or <a style={alwaysUnderlinedLinkStyle}>upload</a> a file
              here
            </p>
          </div>
        </div>
      )}
      {/* Preview Modal */}
      {mediaModalIndex !== false && (
        <Modal
          style={modalStyle}
          onClose={() => setMediaModalIndex(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setMediaModalIndex(false);
            } else {
              onKeyDownMediaModal(
                e,
                mediaModalIndexRef.current,
                setMediaModalIndex,
                previewableFiles
              );
            }
          }}
        >
          <img
            style={{ height: '100%', width: '100%', maxHeight: '80vh' }}
            src={previewableFiles[mediaModalIndex].url}
          />
        </Modal>
      )}
    </div>
  );
};

const onKeyDownMediaModal = (e, index, setMediaModalIndex, files) => {
  if (files.length < 2) {
    return;
  }
  if (e.key === 'ArrowRight' && index + 1 < files.length) {
    return setMediaModalIndex(index + 1);
  }
  if (e.key === 'ArrowLeft' && index - 1 >= 0) {
    return setMediaModalIndex(index - 1);
  }
};
